import { useEffect, useRef } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { tokenUtils } from '@/api/axios.config';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/api/services/auth.service';

/**
 * Syncs Privy auth state with our backend and auth store.
 * When user logs in via Privy, gets token, stores it, and fetches profile.
 */
export function PrivyAuthSync() {
  const { authenticated, getAccessToken, ready } = usePrivy();
  const { setUser, clearUser } = useAuthStore();
  const hasAttemptedSync = useRef(false);

  useEffect(() => {
    if (!ready) return;

    const syncAuth = async () => {
      if (authenticated) {
        try {
          const token = await getAccessToken();
          if (token) {
            tokenUtils.setToken(token);
            if (!hasAttemptedSync.current) {
              hasAttemptedSync.current = true;
              const profileResponse = await authService.getUserProfile();
              setUser(profileResponse.data);
            }
          }
        } catch (error) {
          console.error('Auth sync failed:', error);
          hasAttemptedSync.current = false;
        }
      } else {
        hasAttemptedSync.current = false;
        tokenUtils.removeToken();
        clearUser();
      }
    };

    syncAuth();

    const refreshInterval = authenticated
      ? setInterval(syncAuth, 50 * 60 * 1000)
      : undefined;

    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [authenticated, ready, getAccessToken, setUser, clearUser]);

  return null;
}
